import useIsEmea from "@dana/Common/Hooks/useIsEmea";
import ViewCreditDetails from "@dana/Components/ViewCredit/ViewCreditDetails";
import { PaginationPresentationProps } from "@dana/MobiusOverrides/Pagination/Pagination";
import { themeBackgrounds, themeColors } from "@dana/MobiusOverrides/styles/colors";
import { CreditMemo, CreditMemoDetails } from "@dana/Services/CreditService";
import ApplicationStateDana from "@dana/Store/DanaApplicationState";
import loadCreditMemoDetails from "@dana/Store/Data/ViewCredit/Handlers/ViewCreditDetailsHandler";
import updateCreditFilterFields from "@dana/Store/Data/ViewCredit/Handlers/ViewCreditFilterHandler";
import getCreditMomos, { GetCreditMemosParameter } from "@dana/Store/Data/ViewCredit/Handlers/ViewCreditHandler";
import { formatDecimal } from "@dana/utils/utils";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import getLocalizedDateTime from "@insite/client-framework/Common/Utilities/getLocalizedDateTime";
import translate from "@insite/client-framework/Translate";
import { PaginationModel } from "@insite/client-framework/Types/ApiModels";
import Clickable, { ClickablePresentationProps, ClickableProps } from "@insite/mobius/Clickable";
import DataTable, { DataTableProps, SortOrderOptions } from "@insite/mobius/DataTable";
import DataTableBody from "@insite/mobius/DataTable/DataTableBody";
import DataTableCell, { DataTableCellProps } from "@insite/mobius/DataTable/DataTableCell";
import DataTableHead from "@insite/mobius/DataTable/DataTableHead";
import DataTableHeader, { DataTableHeaderProps } from "@insite/mobius/DataTable/DataTableHeader";
import DataTableRow from "@insite/mobius/DataTable/DataTableRow";
import LoadingOverlay from "@insite/mobius/LoadingOverlay";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import Pagination from "@insite/mobius/Pagination";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useEffect,useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

export interface ViewCreditTableStyles {
    dataTable?: DataTableProps;
    container?: InjectableCss;
    orderDateHeader?: DataTableHeaderProps;
    orderNumberHeader?: DataTableHeaderProps;
    shipToHeader?: DataTableHeaderProps;
    statusHeader?: DataTableHeaderProps;
    customerPOHeader?: DataTableHeaderProps;
    orderTotalHeader?: DataTableHeaderProps;
    reorderHeader?: DataTableHeaderProps;
    orderDateCells?: DataTableCellProps;
    orderNumberCells?: DataTableCellProps;
    shipToCells?: DataTableCellProps;
    statusCells?: DataTableCellProps;
    customerPOCells?: DataTableCellProps;
    creditMemoTotalCell?: DataTableCellProps;
    appliedCreditsCell?: DataTableCellProps;
    openCreditCell?: DataTableCellProps;
    orderTotalCells?: DataTableCellProps;
    reorderCells?: DataTableCellProps;
    creditMemoNumberText?: TypographyPresentationProps;
    creditMemoNumberValue?: TypographyPresentationProps;
    creditMemoNumberWrapper?: InjectableCss;
    viewCreditMemoModalPopUp?: ModalPresentationProps;
    pagination?: PaginationPresentationProps;
    creditMemoNumber?: ClickablePresentationProps;
    loadingModel?: InjectableCss;
    noResultsWrapper?: InjectableCss;
}

export const viewCreditTableStyle: ViewCreditTableStyles = {
    container: {
        css: css`
            overflow: auto;
            max-width: 1440px;
            margin: auto;
            position: relative;
        `,
    },
    noResultsWrapper: {
        css: css`
            padding: 15px;
        `
    },
    loadingModel: {
        css: css`
            height: 100vh;  
        `
    },
    creditMemoTotalCell:{
        css: css`
            text-align: right;
        `
    },

    appliedCreditsCell: {
        css: css`
        text-align: right;
    `
    },
    openCreditCell: {
        css: css`
        text-align: right;
    `
    },
    creditMemoNumber: {
        css: css`
            color: ${themeColors.primary.danaBlue};
        `
    },
    shipToHeader: {
        tight: true,
    },
    orderTotalHeader: {
        tight: true,
        alignX: "right",
    },
    reorderHeader: {
        tight: true,
    },
    orderTotalCells: {
        alignX: "right",
    },
    shipToCells: {
        typographyProps: {
            ellipsis: true,
            css: css`
                display: block;
                max-width: 300px;
            `,
        },
    },
    pagination: {
        cssOverrides: {
            pagination: css`
                @media print {
                    display: none;
                }
            `,
        },
    },
    viewCreditMemoModalPopUp: {
        cssOverrides: {
            modalBody: css`
                background: ${themeBackgrounds.offWhite};
            `,
        },
        closeButtonProps: {
            size: 24,
            css: css`
                background: none;
                top: 8px;
                border: none;
                position: absolute;
                right: 8px;

                &:hover {
                    background: none;
                    border: none;
                }
            `,
        },
    },
    creditMemoNumberText: {
        variant: "h3",
        color: themeBackgrounds.lightGray,
        css: css`
                margin-bottom: 0px;
            `,
    },

    creditMemoNumberValue: {
        variant: "h3",
        color: themeBackgrounds.darkGray,
        css: css`
                margin-bottom: 0px;
            `,
    },

    creditMemoNumberWrapper: {
        css: css`
                display: flex;
                width: 100%;
                gap: 10px;
            `,
    },
};

const styles = viewCreditTableStyle;
const mapStateToProps = (state: ApplicationStateDana) => ({
    viewCreditState: state.viewCreditState,
    viewCreditDetailsState: state.viewCreditDetailsState,
    language: state.context.session.language,
    isEMEARegion: useIsEmea(state),
});

const mapDispatchToProps = {
    updateCreditFilterFields,
    loadCreditMemoDetails,
    getCreditMomos
};
type Props = ResolveThunks<typeof mapDispatchToProps> & ReturnType<typeof mapStateToProps>;

const ViewCreditTable: React.FC<Props> = ({ viewCreditDetailsState, loadCreditMemoDetails, getCreditMomos, updateCreditFilterFields, viewCreditState, language,isEMEARegion }) => {

    const [isChecked, setIsChecked] = useState(false);
    const [isOpenShare, setIsOpenShare] = useState(false);
    const [credits, setCredits] = useState<CreditMemo[]>();
    const [creditMemoDetails, setCreditMemoDetails] = useState<CreditMemoDetails>();
    const [pagination, setPagination] = useState<PaginationModel | null>(viewCreditState?.pagination);
    const [page, setPage] = useState<number>(1);
    const [pageSize, setPageSize] = useState<number>(12);
    const [viewCreditNumber, setCreditMemoNumber] = useState<string>();

    const handleViewCreditModelPopup = (creditNumber: string) => {
        setCreditMemoNumber(creditNumber);
        loadCreditMemoDetails({
            creditMemoNumber: creditNumber
        })
        setIsChecked(true);
    };

    const handleViewCreditModelPopupClose = () => {
        setIsChecked(!isChecked);
    };
    useEffect(() => {
        setCredits(viewCreditState?.creditMemos);
        setPagination(viewCreditState?.pagination)
    }, [viewCreditState]);

    useEffect(() => {
        setCreditMemoDetails(viewCreditDetailsState?.creditMemoDetails);

    }, [viewCreditDetailsState]);

    const doSearch = (filter: GetCreditMemosParameter) => {
        updateCreditFilterFields(filter);
        getCreditMomos(filter)
    }
    const headerClick = (sortField: string) => {
        const previousSort = viewCreditState.filter.sort;
        const direction = previousSort && previousSort === "ASC" ? "DESC" : "ASC";
        const filter = {
            ...viewCreditState.filter,
            sortBy: sortField,
            sort: direction
        }
        doSearch(filter)
    }

    const sorted = (sortField: string) => {
        let sorted: boolean | string = false;

        if (viewCreditState.filter.sortBy !== sortField) {
            return sorted;
        }
        const primaryField = viewCreditState.filter.sort;
        if (primaryField === "ASC") {
            sorted = "ascending";
        } else if (primaryField === "DESC") {
            sorted = "descending";
        }
        return sorted as SortOrderOptions;
    };
    const onChangePage = (page: number) => {
        setPage(page);
        const filter = {
            ...viewCreditState.filter,
            page,
        }
        doSearch(filter)
    };

    const onChangeResultsPerPage = (event: React.ChangeEvent<HTMLSelectElement>) => {
        setPage(1);
        if (Number(event.currentTarget.value)) {
            setPageSize(Number(event.currentTarget.value));
            const filter = {
                ...viewCreditState.filter,
                pageSize: Number(event.currentTarget.value),
            }
            doSearch(filter)
        }
    };



    return (
        <>
        <StyledWrapper {...styles.container} data-test-selector="orderHistoryTable">
            <DataTable {...styles.dataTable}>
                <DataTableHead>
                    <DataTableHeader
                        {...styles.orderNumberHeader}
                        title={translate("Credit Memo No.")}
                        sorted={sorted("CreditMemoNumber")}
                        onSortClick={() => headerClick("CreditMemoNumber")}
                        data-test-selector="viewCreditHeader_creditMemoNumber"
                    >
                        {translate("Credit Memo No.", "Credit Memo No.")}
                    </DataTableHeader>
                    <DataTableHeader
                        {...styles.orderDateHeader}
                        title={translate("Primary PO No.")}
                        sorted={sorted("OriginalPONumber")}
                        onSortClick={() => headerClick("OriginalPONumber")}
                        data-test-selector="viewCreditHeader_PrimaryPONo"
                    >
                        {translate("Primary PO No.", "Primary PO No.")}
                    </DataTableHeader>
                    <DataTableHeader
                        {...styles.statusHeader}
                        title={translate("Secondary PO No.")}
                        sorted={sorted("SecondaryPONumber")}
                        onSortClick={() => headerClick("SecondaryPONumber")}
                        data-test-selector="viewCreditHeader_secondaryPONumber"
                    >
                        {translate("Secondary PO No", "Secondary PO Number")}
                    </DataTableHeader>
                    <DataTableHeader
                        {...styles.statusHeader}
                        title={translate("Part No.")}
                        sorted={sorted("PartNumbers")}
                        onSortClick={() => headerClick("PartNumbers")}
                        data-test-selector="viewCreditHeader_partNumbers"
                    >
                        {translate("Part No.", "Part No.")}
                    </DataTableHeader>
                    <DataTableHeader
                        {...styles.customerPOHeader}
                        title={translate("Credit Memo Date")}
                        sorted={sorted("CreditMemoDate")}
                        onSortClick={() => headerClick("CreditMemoDate")}
                        data-test-selector="viewCreditHeader_creditMemoDate"
                    >
                        {translate("Credit Memo Date", "Credit Memo Date")}
                    </DataTableHeader>
                    <DataTableHeader
                        {...styles.customerPOHeader}
                        title={translate("Credit Memo Total")}
                        sorted={sorted("CreditMemoAmount")}
                        onSortClick={() => headerClick("CreditMemoAmount")}
                    >
                        {translate("Credit Memo Total", "Credit Memo Total")}
                    </DataTableHeader>
                    <DataTableHeader
                        {...styles.customerPOHeader}
                        title={translate("Applied Credits")}
                        sorted={sorted("AppliedCredits")}
                        onSortClick={() => headerClick("AppliedCredits")}
                    >
                        {translate("Applied Credits", "Applied Credits")}
                    </DataTableHeader>
                    <DataTableHeader
                        {...styles.customerPOHeader}
                        title={translate("Open Credits")}
                        sorted={sorted("OpenCredits")}
                        onSortClick={() => headerClick("OpenCredits")}
                    >
                        {translate("Open Credits", "Open Credits")}
                    </DataTableHeader>
                </DataTableHead>
                <DataTableBody data-test-selector="viewCreditTable_tableBody">
                    {credits?.map(
                        ({

                            creditMemoAmount,
                            creditMemoAmountDisplay,
                            appliedCreditsDisplay,
                            openCreditsDisplay,
                            creditMemoDate,
                            creditMemoNumber,
                            originalPONumber,
                            partNumbers,
                            secondaryPONumber,
                            appliedCredits,
                            openCredits
                        }, index) => (
                            <DataTableRow key={index + creditMemoNumber}>
                                <DataTableCell
                                    {...styles.orderNumberCells}
                                    data-test-selector="viewCreditTable_tableCell_creditMemoNumber"
                                >
                                    <Clickable onClick={() => handleViewCreditModelPopup(creditMemoNumber)} {...styles.creditMemoNumber}>
                                        {creditMemoNumber}
                                    </Clickable>

                                </DataTableCell>
                                <DataTableCell
                                    {...styles.orderDateCells}
                                    data-test-selector="viewCreditTable_tableCell_PrimaryPONo"
                                >
                                    {originalPONumber ? originalPONumber?.trim() : translate("N/A")}
                                </DataTableCell>

                                <DataTableCell
                                    {...styles.statusCells}
                                    data-test-selector="viewCreditTable_tableCell_secondaryPONumber"
                                >
                                    {secondaryPONumber ? secondaryPONumber?.trim() : translate("N/A")}
                                </DataTableCell>
                                <DataTableCell
                                    {...styles.statusCells}
                                    data-test-selector="viewCreditTable_tableCell_PartNumber"
                                >
                                    {partNumbers.join(',')}
                                </DataTableCell>
                                <DataTableCell {...styles.customerPOCells}>

                                    {creditMemoDate
                                        ? getLocalizedDateTime({
                                            dateTime: new Date(creditMemoDate),
                                            language,
                                        })
                                        : ""}
                                </DataTableCell>
                                <DataTableCell {...styles.creditMemoTotalCell}>
                                    {isEMEARegion?formatDecimal(creditMemoAmount,{locale:"fr-FR"}):formatDecimal(creditMemoAmount)}
                                </DataTableCell>
                                <DataTableCell {...styles.appliedCreditsCell}>
                                    {isEMEARegion?formatDecimal(appliedCredits,{locale:"fr-FR"}):formatDecimal(appliedCredits)}
                                </DataTableCell>
                                <DataTableCell {...styles.openCreditCell}>
                                    {isEMEARegion?formatDecimal(openCredits,{locale:"fr-FR"}):formatDecimal(openCredits)}
                                </DataTableCell>
                            </DataTableRow>
                        ),
                    )}
                </DataTableBody>
            </DataTable>
            {credits?.length === 0 && !viewCreditState.isLoading && (
                <StyledWrapper {...styles.noResultsWrapper}>
                    <Typography as="p">{translate("No credits found.")}</Typography>
                </StyledWrapper>
            )}
           
            {isChecked && viewCreditNumber}
            <Modal
                isOpen={isChecked}
                handleClose={handleViewCreditModelPopupClose}
                headline={
                    <StyledWrapper {...styles.creditMemoNumberWrapper}>
                        <Typography {...styles.creditMemoNumberText}> {translate("Credit Memo for")} </Typography>
                        <Typography {...styles.creditMemoNumberValue}>{viewCreditNumber}</Typography>
                    </StyledWrapper>
                }
                size={1014}
                {...styles.viewCreditMemoModalPopUp}
            >

                <LoadingOverlay loading={viewCreditDetailsState.isLoading} {...styles.loadingModel}>
                    {!viewCreditDetailsState.isLoading && creditMemoDetails && <ViewCreditDetails></ViewCreditDetails>}
                </LoadingOverlay>

            </Modal>
        </StyledWrapper>
         {credits && credits?.length > 0 && pagination && (
            <Pagination
                {...styles.pagination}
                currentPage={pagination.currentPage}
                resultsPerPage={pagination.pageSize}
                resultsCount={pagination.totalItemCount}
                resultsPerPageOptions={pagination.pageSizeOptions}
                onChangePage={onChangePage}
                resultsPerPageLabel={translate("Display")}
                onChangeResultsPerPage={onChangeResultsPerPage}
                prevPage={pagination.prevPageUri}
                nextPage={pagination.nextPageUri}
            />
        )}
        </>
    );
};
export default connect(mapStateToProps, mapDispatchToProps)(ViewCreditTable);
